import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const SHORT = {
  "Demandas e carga de trabalho": "Demandas",
  "Controle, autonomia e clareza": "Controle",
  "Liderança e apoio da supervisão": "Liderança",
  "Relações interpessoais e clima": "Relações",
  "Reconhecimento, justiça e segurança psicológica": "Reconhecimento",
  "Condições do ambiente físico e recursos de trabalho": "Ambiente",
  "Saúde mental, bem-estar e sinais de sofrimento": "Saúde mental",
  "Assédio, violência e condutas inadequadas": "Assédio",
  "Organização da produção e períodos de maior demanda": "Produção",
};

function cellClass(level) {
  return level === 0 ? "risk-0" : `risk-${level}`;
}

function LongitudinalChart({ series }) {
  // SVG line chart of overall_score over time. Score axis 0..5.
  const W = 720, H = 220, PAD_L = 40, PAD_R = 20, PAD_T = 16, PAD_B = 40;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const n = series.length;
  const xAt = (i) => PAD_L + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const yAt = (v) => PAD_T + innerH - (v / 5) * innerH;
  const points = series.map((s, i) => `${xAt(i)},${yAt(s.overall_score || 0)}`).join(" ");

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[600px]" data-testid="longitudinal-chart">
        {/* Y grid lines */}
        {[0, 1, 2, 3, 4, 5].map((v) => (
          <g key={v}>
            <line x1={PAD_L} x2={W - PAD_R} y1={yAt(v)} y2={yAt(v)} stroke="#e2e8f0" strokeWidth="1" />
            <text x={PAD_L - 8} y={yAt(v) + 4} textAnchor="end" fontSize="10" fill="#64748b" fontFamily="monospace">{v}</text>
          </g>
        ))}
        {/* Risk band background (Substancial+Intolerável) */}
        <rect x={PAD_L} y={yAt(5)} width={innerW} height={yAt(3.5) - yAt(5)} fill="#fee2e2" opacity="0.35" />
        {/* Line */}
        <polyline points={points} fill="none" stroke="#007040" strokeWidth="2.5" />
        {/* Dots + labels */}
        {series.map((s, i) => (
          <g key={s.assessment_id}>
            <circle cx={xAt(i)} cy={yAt(s.overall_score || 0)} r="5" fill="#007040" />
            <text x={xAt(i)} y={yAt(s.overall_score || 0) - 10}
                  textAnchor="middle" fontSize="11" fill="#0f172a" fontFamily="monospace" fontWeight="700">
              {s.overall_score || "—"}
            </text>
            <text x={xAt(i)} y={H - 18} textAnchor="middle" fontSize="10" fill="#475569">
              {new Date(s.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}
            </text>
            <text x={xAt(i)} y={H - 4} textAnchor="middle" fontSize="9" fill="#94a3b8">
              {(s.titulo || "").slice(0, 18)}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export default function CompanyHeatMap() {
  const { id } = useParams();
  const [groupBy, setGroupBy] = useState("ghe");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [longitudinal, setLongitudinal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: lg }] = await Promise.all([
        api.get(`/companies/${id}/heatmap`, { params: { group_by: groupBy } }),
        api.get(`/companies/${id}/longitudinal`),
      ]);
      setData(d);
      setLongitudinal(lg);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Falha ao carregar o mapa de calor";
      toast.error(msg);
      // Set empty data so the page exits the spinner state and shows the empty UI.
      setData({ company: null, groups: [], dimensions: [], group_by: groupBy });
      setLongitudinal(null);
    } finally {
      setLoading(false);
    }
  }, [id, groupBy]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return <div className="p-12 text-slate-500"><i className="fa-solid fa-circle-notch fa-spin mr-2"></i> Carregando…</div>;
  }

  // Render guard: when the request failed, `company` will be null. Show a
  // friendly empty state instead of crashing on company.razao_social below.
  if (!data.company) {
    return (
      <div className="p-12 max-w-2xl mx-auto" data-testid="heatmap-not-found">
        <Link to="/app/empresas" className="text-sm text-slate-500 hover:text-emerald-900">
          ← Voltar para empresas
        </Link>
        <div className="card mt-6 text-center py-12">
          <i className="fa-solid fa-triangle-exclamation text-4xl text-amber-500 mb-4"></i>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 mb-2">
            Empresa não encontrada
          </h1>
          <p className="text-sm text-slate-600">
            Verifique o endereço ou volte à lista de empresas. Se você acabou de criá-la,
            tente abrir novamente pela tela de listagem.
          </p>
        </div>
      </div>
    );
  }

  const { company, groups, dimensions } = data;

  return (
    <div className="p-8 lg:p-12 max-w-[1400px] mx-auto">
      <Link to="/app/empresas" className="text-sm text-slate-500 hover:text-emerald-900" data-testid="heatmap-back">
        ← Voltar para empresas
      </Link>

      <div className="mt-4 flex flex-wrap justify-between items-end gap-4 mb-8">
        <div>
          <div className="overline mb-2">Mapa de calor comparativo</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900">{company.razao_social}</h1>
          <p className="text-slate-600 mt-2">
            Comparativo de risco por dimensão psicossocial entre {groups.length} grupo(s).
          </p>
        </div>
        <div className="flex gap-2">
          <Link to={`/app/empresas/${company.id}/mapa-risco`}
                className="btn btn-primary text-sm" data-testid="company-risk-map-button">
            <i className="fa-solid fa-shield-halved"></i> Mapa de Risco Consolidado
          </Link>
          <Link to={`/app/importar/pgr/${company.id}`}
                className="btn btn-ghost border border-emerald-300 text-emerald-900 hover:bg-emerald-50 text-sm"
                data-testid="company-complement-pgr-button">
            <i className="fa-solid fa-wand-magic-sparkles"></i> Complementar via PGR
          </Link>
        </div>
      </div>

      {/* Group-by selector */}
      <div className="card mb-6">
        <div className="overline mb-3">Agrupar por</div>
        <div className="flex flex-wrap gap-2">
          {[
            ["geral",      "Geral",       "fa-globe"],
            ["ghe",        "GHE",         "fa-layer-group"],
            ["setor",      "Setor",       "fa-sitemap"],
            ["individual", "Individual",  "fa-user"],
          ].map(([k, label, icon]) => (
            <button key={k}
                    onClick={() => setGroupBy(k)}
                    data-testid={`heatmap-group-${k}`}
                    className={`btn ${groupBy === k ? "btn-primary" : "btn-secondary"} text-sm`}>
              <i className={`fa-solid ${icon}`}></i> {label}
            </button>
          ))}
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="card text-center py-16 text-slate-500">
          <i className="fa-solid fa-chart-area text-4xl text-slate-300 mb-3"></i>
          <p>Nenhuma avaliação ainda. Crie uma avaliação e colete respostas.</p>
        </div>
      ) : (
        <>
          {/* Heat map grid */}
          <div className="card overflow-x-auto">
            <div className="overline mb-4">Mapa de calor — Score por dimensão</div>
            <table className="w-full text-xs border-collapse min-w-[1100px]">
              <thead>
                <tr>
                  <th className="text-left p-2 sticky left-0 bg-white border-b-2 border-slate-300 font-bold uppercase text-[10px] tracking-wider">Grupo</th>
                  <th className="text-center p-2 border-b-2 border-slate-300 font-bold uppercase text-[10px] tracking-wider">Avaliações</th>
                  <th className="text-center p-2 border-b-2 border-slate-300 font-bold uppercase text-[10px] tracking-wider">Score geral</th>
                  {dimensions.map((d) => (
                    <th key={d} className="text-center p-2 border-b-2 border-slate-300 font-bold uppercase text-[10px] tracking-wider">
                      {SHORT[d] || d}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <tr key={g.key} data-testid={`heatmap-row-${g.key}`}>
                    <td className="p-2 sticky left-0 bg-white border-b border-slate-100 font-semibold text-slate-900 max-w-[220px]">
                      {g.label}
                    </td>
                    <td className="text-center p-2 border-b border-slate-100 font-mono text-slate-600">
                      {g.assessment_count} · <span className="text-emerald-900">{g.response_count} resp.</span>
                    </td>
                    <td className="p-1 border-b border-slate-100">
                      <div className={`${cellClass(g.overall.level)} border text-center py-2 font-bold`}>
                        {g.overall_score || "—"}
                        <div className="text-[9px] uppercase font-bold opacity-80">{g.overall.label}</div>
                      </div>
                    </td>
                    {g.dimensions.map((d) => (
                      <td key={d.dimensao} className="p-1 border-b border-slate-100">
                        <div className={`${cellClass(d.responses ? d.level : 0)} border text-center py-2 font-mono`}
                             title={`${d.dimensao}\nScore: ${d.score}\n${d.label}`}>
                          {d.responses ? d.score : "—"}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Legend */}
            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs">
              <span className="overline">Legenda AIHA:</span>
              {[
                ["Baixo", "risk-1"],
                ["Tolerável", "risk-2"],
                ["Moderado", "risk-3"],
                ["Substancial", "risk-4"],
                ["Intolerável", "risk-5"],
                ["Sem dados", "risk-0"],
              ].map(([l, c]) => (
                <span key={l} className={`${c} border px-2 py-1 font-bold uppercase tracking-wider`}>{l}</span>
              ))}
            </div>
          </div>

          {/* Ranking cards */}
          <div className="mt-6 card">
            <div className="overline mb-4">Ranking dos grupos (score geral)</div>
            <div className="space-y-2">
              {groups.map((g, i) => (
                <div key={g.key} className="flex items-center gap-3">
                  <span className="font-mono text-xs text-slate-400 w-6">#{i + 1}</span>
                  <span className="font-semibold text-slate-900 w-48 truncate">{g.label}</span>
                  <div className="flex-1 h-6 bg-slate-100 relative">
                    <div className={`${cellClass(g.overall.level)} border h-full`}
                         style={{ width: `${Math.min(100, (g.overall_score / 5) * 100)}%` }}>
                    </div>
                  </div>
                  <span className="font-mono text-sm font-bold text-slate-900 w-12 text-right">{g.overall_score}</span>
                  <span className="text-[10px] uppercase font-bold text-slate-500 w-24 text-right">{g.overall.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Longitudinal evolution */}
          {longitudinal && longitudinal.series && longitudinal.series.length > 1 && (
            <div className="mt-6 card" data-testid="longitudinal-section">
              <div className="overline mb-1">Evolução temporal · Score geral</div>
              <p className="text-xs text-slate-500 mb-4">
                Comparativo entre as {longitudinal.series.length} avaliações realizadas para esta empresa.
              </p>
              <LongitudinalChart series={longitudinal.series} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
